import { rd } from '@passionware/monads';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCustomFieldDefinitions } from 'store/v1/custom_fields/custom_fields.selectors';
import { generateCalloutFieldMapper } from 'v1/helpers/calloutFieldHelper';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { MergeServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';

/**
 * Callout field mapper uses the callout's entity workspace (catalog).
 * Overlay write-lens ids follow browsing URL — applicant → resource create
 * stamps membership / CF values for the workspace in the address bar.
 */
export function useCalloutFieldMappings(
  resourceTypeId: number | null | undefined,
  enterpriseWorkspaceId: number | null | undefined,
  i18n: Parameters<typeof generateCalloutFieldMapper>[2],
  services: MergeServices<
    [
      WithAppScopeService,
      WithMetastructureMappingService,
      WithGlobalSettingsService
    ]
  >
) {
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const fieldsRequest = rd.tryMap(scopeRd, resolved =>
    resourceTypeId == null
      ? undefined
      : {
          type: 'resource' as const,
          subtypeId: resourceTypeId,
          scope: appScopeUtils.identityFromEntityWorkspace(
            resolved.organizationId,
            enterpriseWorkspaceId
          )
        }
  );
  const browsingFieldsRequest = rd.tryMap(scopeRd, resolved =>
    resourceTypeId == null
      ? undefined
      : {
          type: 'resource' as const,
          subtypeId: resourceTypeId,
          scope: appScopeUtils.identityFromResolved(resolved)
        }
  );
  const fieldsRd =
    services.metastructureMappingService.useMetastructure(fieldsRequest);
  const overlayDefinitionIdsRd =
    services.metastructureMappingService.useOverlayCustomFieldDefinitionIds(
      browsingFieldsRequest
    );
  const fields = rd.getOrElse(rd.useLastWithPlaceholder(fieldsRd), []);
  const customFieldDefinitions =
    (useSelector(getCustomFieldDefinitions) as unknown[]) ?? [];

  const pronouns = services.globalSettingsService.usePronounsOptions();

  return useMemo(() => {
    const fieldMapper = generateCalloutFieldMapper(
      fields,
      customFieldDefinitions,
      i18n,
      { pronouns }
    );
    const overlayLensReady = rd.isSuccess(overlayDefinitionIdsRd);
    return {
      fieldMapper,
      overlayLensReady,
      overlayDefinitionIds: overlayLensReady
        ? rd.tryGet(overlayDefinitionIdsRd)
        : undefined
    };
  }, [fields, customFieldDefinitions, i18n, pronouns, overlayDefinitionIdsRd]);
}
